// conectagran/frontend/src/pages/AdManager/GamTable.tsx
import { useState } from 'react'
import type { GamReportRow } from '@/api/hooks/useGam'

interface Props {
  rows: GamReportRow[]
}

type SortKey = keyof GamReportRow

const STATUS_BADGES: Record<string, { label: string; bg: string; text: string }> = {
  active:   { label: 'Ativo',    bg: 'bg-[#4ade8020]', text: 'text-[#4ade80]' },
  idle:     { label: 'Ocioso',   bg: 'bg-[#fbbf2420]', text: 'text-[#fbbf24]' },
  inactive: { label: 'Inativo',  bg: 'bg-[#f8717120]', text: 'text-[#f87171]' },
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString('pt-BR')
}

export function GamTable({ rows }: Props) {
  const [sortKey, setSortKey]  = useState<SortKey>('ecpm')
  const [sortDir, setSortDir]  = useState<'asc' | 'desc'>('desc')

  const hasUnfilled = rows.some(r => r.code_served !== undefined && r.code_served > 0)
  const hasStatus   = rows.some(r => r.status !== undefined)

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = [...rows].sort((a, b) => {
    const va = (a[sortKey] ?? 0) as number
    const vb = (b[sortKey] ?? 0) as number
    return sortDir === 'desc' ? vb - va : va - vb
  })

  const maxRevenue  = Math.max(...rows.map(r => r.revenue_brl), 1)
  const peakEcpm    = rows.reduce((m, r) => (r.ecpm > m ? r.ecpm : m), 0)
  const dropEcpm    = rows.filter(r => r.impressions > 100)
                          .reduce((m, r) => (r.ecpm < m ? r.ecpm : m), Infinity)

  function ecpmClass(ecpm: number) {
    if (ecpm >= peakEcpm * 0.8) return 'text-[#22c55e] font-semibold'
    if (ecpm <= dropEcpm * 1.2 && dropEcpm < Infinity) return 'text-[#ef4444] font-semibold'
    return 'text-[#c9a84c] font-semibold'
  }

  function fillRateClass(rate: number) {
    if (rate >= 80) return 'text-[#4ade80]'
    if (rate >= 50) return 'text-[#fbbf24]'
    return 'text-[#f87171]'
  }

  const Th = ({ label, sortable, k, left }: { label: string; sortable?: boolean; k?: SortKey; left?: boolean }) => (
    <th
      className={`px-3 py-2.5 ${left ? 'text-left' : 'text-right'} text-[11px] font-semibold text-[#64748b] uppercase tracking-widest border-b border-[#1e2235] whitespace-nowrap ${sortable ? 'cursor-pointer hover:text-[#c9a84c] select-none' : ''}`}
      onClick={sortable && k ? () => toggleSort(k) : undefined}
    >
      {label}
      {sortable && k && sortKey === k && (
        <span className="ml-1 text-[#c9a84c]">{sortDir === 'desc' ? '↓' : '↑'}</span>
      )}
    </th>
  )

  const totals = rows.reduce(
    (acc, r) => ({
      impressions:  acc.impressions + r.impressions,
      clicks:       acc.clicks + r.clicks,
      code_served:  acc.code_served + (r.code_served ?? 0),
      unfilled:     acc.unfilled + (r.unfilled ?? 0),
      revenue_brl:  acc.revenue_brl + r.revenue_brl,
    }),
    { impressions: 0, clicks: 0, code_served: 0, unfilled: 0, revenue_brl: 0 }
  )
  const avgCtr      = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0
  const avgEcpm     = totals.impressions > 0
    ? rows.reduce((sum, r) => sum + r.ecpm * r.impressions, 0) / totals.impressions
    : 0
  const avgFillRate = totals.code_served > 0 ? (totals.impressions / totals.code_served) * 100 : (totals.impressions > 0 ? 100 : 0)

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#0b0d14]">
            <Th label="Hora / Período" left />
            {hasStatus && <Th label="Status" />}
            <Th label="Impressões" sortable k="impressions" />
            {hasUnfilled && <Th label="Requests" sortable k="code_served" />}
            {hasUnfilled && <Th label="N. Preenchidas" sortable k="unfilled" />}
            {hasUnfilled && <Th label="Fill Rate" sortable k="fill_rate" />}
            <Th label="Cliques"    sortable k="clicks" />
            <Th label="CTR"        sortable k="ctr" />
            <Th label="eCPM"       sortable k="ecpm" />
            <Th label="Receita"    sortable k="revenue_brl" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isPeak = row.ecpm === peakEcpm && peakEcpm > 0
            const isDrop = row.impressions > 100 && row.ecpm === dropEcpm && dropEcpm < Infinity
            const badge  = row.status ? STATUS_BADGES[row.status] : null

            return (
              <tr key={row.label} className="border-b border-[#13151f] hover:bg-[#0f1119] transition-colors">
                <td className="px-3 py-2.5 text-left text-sm text-[#cbd5e1]">
                  {row.label}
                  {isPeak && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#4ade8020] text-[#4ade80]">pico</span>
                  )}
                  {isDrop && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#f8717120] text-[#f87171]">queda</span>
                  )}
                </td>
                {hasStatus && (
                  <td className="px-3 py-2.5 text-right text-sm">
                    {badge && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#94a3b8]">
                  {fmtNum(row.impressions)}
                </td>
                {hasUnfilled && (
                  <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#64748b]">
                    {fmtNum(row.code_served ?? 0)}
                  </td>
                )}
                {hasUnfilled && (
                  <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#f87171]">
                    {(row.unfilled ?? 0) > 0 ? fmtNum(row.unfilled!) : '—'}
                  </td>
                )}
                {hasUnfilled && (
                  <td className={`px-3 py-2.5 text-right text-sm tabular-nums font-semibold ${fillRateClass(row.fill_rate ?? 0)}`}>
                    {(row.code_served ?? 0) > 0 ? `${(row.fill_rate ?? 0).toFixed(1)}%` : '—'}
                  </td>
                )}
                <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#94a3b8]">
                  {fmtNum(row.clicks)}
                </td>
                <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#94a3b8]">
                  {row.ctr.toFixed(2)}%
                </td>
                <td className={`px-3 py-2.5 text-right text-sm tabular-nums ${ecpmClass(row.ecpm)}`}>
                  ${row.ecpm.toFixed(2)}
                </td>
                <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[#94a3b8]">
                  <div className="flex items-center justify-end gap-2">
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${Math.round((row.revenue_brl / maxRevenue) * 72)}px`,
                        background: isDrop ? '#f87171' : '#c9a84c',
                        minWidth: '2px',
                        opacity: 0.75,
                      }}
                    />
                    R$ {row.revenue_brl.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="bg-[#0b0d14] border-t-2 border-[#1e2235]">
            <td className="px-3 py-2.5 text-left text-sm font-semibold text-[#f8fafc]">Total / Média</td>
            {hasStatus && <td />}
            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#f8fafc]">
              {fmtNum(totals.impressions)}
            </td>
            {hasUnfilled && (
              <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#64748b]">
                {fmtNum(totals.code_served)}
              </td>
            )}
            {hasUnfilled && (
              <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#f87171]">
                {totals.unfilled > 0 ? fmtNum(totals.unfilled) : '—'}
              </td>
            )}
            {hasUnfilled && (
              <td className={`px-3 py-2.5 text-right text-sm font-semibold tabular-nums ${fillRateClass(avgFillRate)}`}>
                {totals.code_served > 0 ? `${avgFillRate.toFixed(1)}%` : '—'}
              </td>
            )}
            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#f8fafc]">
              {fmtNum(totals.clicks)}
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#f8fafc]">
              {avgCtr.toFixed(2)}%
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#c9a84c]">
              ${avgEcpm.toFixed(2)}
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-[#f8fafc]">
              R$ {totals.revenue_brl.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
